import { useCallback, useEffect, useState } from "react";
import { api } from "../api";
import { FormError } from "../components/Form";
import { config } from "../config";
import { GoogleButton } from "../components/GoogleButton";
import { AuthCard, Layout, Loading } from "../components/Layout";
import {
  forgetDesktopRequest,
  loopbackUrl,
  parseDesktopRequest,
  pendingDesktopRequest,
  rememberDesktopRequest,
  type DesktopRequest,
} from "../desktop";
import { errorMessage } from "../messages";
import { Link, Redirect, useRouter } from "../router";
import { accountComplete, useSession } from "../session";

/**
 * The desktop app's sign-in: the customer signs in here (Google or email) and
 * the app on their Mac is signed in too. See desktop.ts for the handoff.
 */
export function Desktop() {
  const { session, token, signOut } = useSession();
  const { navigate } = useRouter();
  const [request, setRequest] = useState<DesktopRequest | null>(() => pendingDesktopRequest());
  const [step, setStep] = useState<"idle" | "connecting" | "connected" | "failed">("idle");
  const [error, setError] = useState<string | null>(null);

  // A new request from the app comes in the query string. It is kept in this
  // tab, so it survives signing up or in, and the address bar is tidied.
  // If the tab was already signed in, the customer confirms the account first.
  useEffect(() => {
    if (session.status === "loading") {
      return;
    }
    const fresh = parseDesktopRequest(location.search);
    if (fresh) {
      const next = { ...fresh, confirm: session.status === "signed-in" };
      rememberDesktopRequest(next);
      setRequest(next);
      navigate("/desktop", { replace: true });
    }
  }, [session.status, navigate]);

  const connect = useCallback(
    async (current: DesktopRequest) => {
      setStep("connecting");
      setError(null);
      try {
        const { code } = await api.desktopHandoff(await token(), current.challenge);
        forgetDesktopRequest();
        setStep("connected");
        location.assign(loopbackUrl(current, code));
      } catch (failure) {
        setError(errorMessage(failure));
        setStep("failed");
      }
    },
    [token],
  );

  const me = session.status === "signed-in" ? session.me : null;
  const complete = accountComplete(me);

  // Signed in during this visit: no need to ask again.
  useEffect(() => {
    if (request && complete && !request.confirm && step === "idle") {
      void connect(request);
    }
  }, [request, complete, step, connect]);

  const switchAccount = async () => {
    if (request) {
      const next = { ...request, confirm: false };
      rememberDesktopRequest(next);
      setRequest(next);
    }
    await signOut();
  };

  if (step === "connecting" || step === "connected") {
    return (
      <Layout>
        <AuthCard title={step === "connected" ? "חוזרים לתוכנה…" : "מחברים את התוכנה…"}>
          <Loading />
        </AuthCard>
      </Layout>
    );
  }

  if (!request) {
    return (
      <Layout>
        <AuthCard title="הקישור מהתוכנה כבר לא בתוקף">
          <p>חזרו לתוכנת FrameFlow במחשב ולחצו שוב על ההתחברות.</p>
        </AuthCard>
      </Layout>
    );
  }

  if (session.status === "loading" || (session.status === "signed-in" && !session.me && !session.error)) {
    return <Loading />;
  }

  if (session.status === "signed-out") {
    return (
      <Layout>
        <AuthCard title="התחברות לתוכנת FrameFlow" subtitle="מתחברים כאן, והתוכנה במחשב שלכם מתחברת יחד איתכם.">
          <GoogleButton onError={setError} divider={false} />
          <FormError message={error} />
          {config.googleSignIn && (
            <div className="divider">
              <span>או</span>
            </div>
          )}
          <Link to="/login" className="button button-secondary">
            התחברות עם אימייל וסיסמה
          </Link>
          <p className="switch">
            אין לכם חשבון? <Link to="/signup">פתיחת חשבון</Link>
          </p>
        </AuthCard>
      </Layout>
    );
  }

  if (!complete) {
    return <Redirect to="/complete-profile" />;
  }

  return (
    <Layout>
      <AuthCard
        title="לחבר את תוכנת FrameFlow?"
        subtitle={
          <>
            התוכנה במחשב תתחבר לחשבון <span dir="ltr">{session.user.email}</span>.
          </>
        }
      >
        <FormError message={error} />
        <button type="button" className="button button-primary" onClick={() => void connect(request)}>
          {step === "failed" ? "לנסות שוב" : "חיבור התוכנה"}
        </button>
        <p className="switch">
          <button type="button" className="link-button" onClick={() => void switchAccount()}>
            להתחבר עם חשבון אחר
          </button>
        </p>
      </AuthCard>
    </Layout>
  );
}
